import { applyDelta, type Bootstrap, type ClientMessage, type Inventory, type ServerMessage, type WorldState } from '../shared/protocol.ts';

export type ConnectionStatus = 'connecting' | 'syncing' | 'online' | 'reconnecting' | 'offline' | 'access-required' | 'closed';
type RequestMessage = Extract<ClientMessage, {requestId: string}>;
export type PartyCommand = RequestMessage extends infer T ? T extends RequestMessage ? Omit<T, 'requestId'> : never : never;
export type CommandAck = Extract<ServerMessage, {type: 'ack'}>;
export type NetworkStats = { latencyMs: number | null; receivedBytes: number; sentBytes: number; receivedPerSecond: number; sentPerSecond: number; reconnects: number; messagesIn: number; messagesOut: number };
export type NetworkCallbacks = {
  bootstrap: (value: Bootstrap) => void;
  state: (value: WorldState, selfId: string, serverOffsetMs: number) => void;
  inventory: (value: Inventory) => void;
  status: (value: ConnectionStatus, attempt: number) => void;
  error: (message: string, code?: string) => void;
  pending?: () => void;
};

export class PartyRequestError extends Error {
  constructor(message: string, public readonly code: string, public readonly retryAfterMs = 0) { super(message); this.name = 'PartyRequestError'; }
}

type Pending = { resolve: (ack: CommandAck) => void; reject: (error: Error) => void; timer: ReturnType<typeof setTimeout>; key: string };
const commandTimeout = 12_000;

/** Cookie-authenticated WebSocket transport. Commands are never replayed after an uncertain result. */
export class PartyNetwork {
  private socket: WebSocket | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private snapshotTimer: ReturnType<typeof setTimeout> | null = null;
  private pending = new Map<string, Pending>();
  private stopped = false;
  private attempt = 0;
  private generation = 0;
  private accessCode: string | undefined;
  private waitingForSnapshot = true;
  private pingSentAt = 0;
  private received = 0;
  private sent = 0;
  private messagesIn = 0;
  private messagesOut = 0;
  private sampleReceived = 0;
  private sampleSent = 0;
  private sampledAt = performance.now();
  private encoder = new TextEncoder();
  private measuredLatency: number | null = null;
  private reconnectCount = 0;
  private hasClock = false;
  status: ConnectionStatus = 'connecting';
  bootstrap: Bootstrap | null = null;
  world: WorldState | null = null;
  serverOffsetMs = 0;

  constructor(private readonly callbacks: NetworkCallbacks) {
    document.addEventListener('visibilitychange', this.visibilityChanged);
    window.addEventListener('online', this.becameOnline);
    window.addEventListener('offline', this.becameOffline);
  }

  get connected() { return this.status === 'online' && this.socket?.readyState === WebSocket.OPEN; }
  get serverNow() { return Date.now() + this.serverOffsetMs; }
  hasPending(key: string) { return [...this.pending.values()].some(item => item.key === key); }

  async start(accessCode?: string): Promise<void> {
    this.stopped = false;
    if (accessCode !== undefined) this.accessCode = accessCode;
    this.clearReconnect();
    const generation = ++this.generation;
    this.closeSocket();
    this.setStatus('connecting');
    try {
      await this.loadBootstrap(generation);
      if (this.stopped || generation !== this.generation) return;
      this.openSocket(generation);
    } catch (error) {
      if (this.stopped || generation !== this.generation) return;
      if (error instanceof PartyRequestError && (error.code === 'ACCESS_REQUIRED' || error.code === 'ACCESS_CODE_REQUIRED' || error.code === 'UNAUTHORIZED')) {
        this.setStatus('access-required');
      } else this.scheduleReconnect();
      throw error;
    }
  }

  async retry(): Promise<void> {
    try { await this.start(); } catch (error) { this.callbacks.error(error instanceof Error ? error.message : 'The party could not be reached. Please try again.'); }
  }

  private async loadBootstrap(generation: number) {
    const started = performance.now();
    let response = await fetch('/api/bootstrap', { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(12_000) });
    if (response.status === 401) {
      response = await fetch('/api/session', {
        method: 'POST', credentials: 'same-origin', cache: 'no-store',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(this.accessCode ? { accessCode: this.accessCode } : {}),
        signal: AbortSignal.timeout(12_000),
      });
    }
    let body: unknown;
    try { body = await response.json(); } catch { throw new PartyRequestError('The party returned an unreadable response. Please try again.', 'INVALID_RESPONSE'); }
    if (!response.ok) {
      const data = body as { message?: string; error?: string; code?: string; retryAfterMs?: number };
      const code = response.status === 401 || data.code === 'ACCESS_CODE_REQUIRED' || data.code === 'ACCESS_CODE_INVALID' ? 'ACCESS_REQUIRED' : data.code ?? 'HTTP_ERROR';
      throw new PartyRequestError(data.message || data.error || 'The party could not be opened.', code, data.retryAfterMs);
    }
    const data = body as Bootstrap;
    if (!data || typeof data.selfId !== 'string' || !data.config || !data.inventory || !Number.isFinite(data.serverTime)) throw new PartyRequestError('The party is still getting ready. Please refresh in a moment.', 'INVALID_RESPONSE');
    if (this.stopped || generation !== this.generation) return;
    const roundTrip = performance.now() - started;
    this.updateClock(data.serverTime, roundTrip / 2);
    this.bootstrap = data;
    this.callbacks.bootstrap(data);
    this.callbacks.inventory(data.inventory);
  }

  private openSocket(generation: number) {
    const url = new URL('/api/ws', window.location.href);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    const socket = new WebSocket(url);
    this.socket = socket;
    this.waitingForSnapshot = true;
    socket.addEventListener('open', () => {
      if (this.stopped || this.socket !== socket || generation !== this.generation) { socket.close(); return; }
      this.setStatus('syncing');
      this.armSnapshotTimeout();
      this.sendRaw({ type: 'visibility', visible: !document.hidden });
      this.ping();
      this.heartbeat = setInterval(() => this.ping(), 20_000);
    });
    socket.addEventListener('message', event => {
      if (this.socket !== socket || generation !== this.generation || typeof event.data !== 'string') return;
      this.received += this.encoder.encode(event.data).byteLength;
      this.messagesIn++;
      if (event.data === 'pong') {
        if (this.pingSentAt > 0) { this.measuredLatency = Math.max(0, performance.now() - this.pingSentAt); this.pingSentAt = 0; }
        return;
      }
      let message: ServerMessage;
      try { message = JSON.parse(event.data) as ServerMessage; } catch { this.requestResync(); return; }
      this.receive(message);
    });
    socket.addEventListener('error', () => { /* The close event owns reconnect scheduling. */ });
    socket.addEventListener('close', () => {
      if (this.socket !== socket || generation !== this.generation) return;
      this.socket = null;
      this.clearHeartbeat();
      this.rejectPending('The connection changed before confirmation. Check the party before trying that action again.');
      if (!this.stopped) this.scheduleReconnect();
    });
  }

  private receive(message: ServerMessage) {
    if (!message || typeof message !== 'object' || typeof message.type !== 'string') return;
    if (message.type === 'snapshot') {
      if (!Number.isInteger(message.seq) || !Number.isFinite(message.serverTime) || !Array.isArray(message.avatars) || !Array.isArray(message.chat) || !Array.isArray(message.wishes) || !Array.isArray(message.balloons) || !Array.isArray(message.effects)) {
        this.requestResync(); return;
      }
      this.world = { seq: message.seq, serverTime: message.serverTime, avatars: message.avatars, chat: message.chat, wishes: message.wishes, balloons: message.balloons, effects: message.effects, celebration: message.celebration };
      if (this.bootstrap) this.bootstrap = { ...this.bootstrap, selfId: message.selfId, appearance: message.appearance, name: message.name, inventory: message.inventory, serverTime: message.serverTime };
      this.waitingForSnapshot = false;
      this.clearSnapshotTimeout();
      this.attempt = 0;
      this.updateClock(message.serverTime);
      this.setStatus('online');
      this.callbacks.inventory(message.inventory);
      this.callbacks.state(this.world, message.selfId, this.serverOffsetMs);
    } else if (message.type === 'delta') {
      if (!this.world || this.waitingForSnapshot) { this.requestResync(); return; }
      if (!Number.isInteger(message.seq) || message.seq <= this.world.seq) return;
      if (message.seq !== this.world.seq + 1) { this.requestResync(); return; }
      this.world = applyDelta(this.world, message);
      this.updateClock(message.serverTime);
      this.callbacks.state(this.world, this.bootstrap!.selfId, this.serverOffsetMs);
    } else if (message.type === 'inventory') {
      if (this.bootstrap) this.bootstrap.inventory = message.inventory;
      this.callbacks.inventory(message.inventory);
    } else if (message.type === 'ack') {
      const pending = this.pending.get(message.requestId);
      if (!pending) return;
      clearTimeout(pending.timer); this.pending.delete(message.requestId); pending.resolve(message); this.callbacks.pending?.();
    } else if (message.type === 'error') {
      const pending = message.requestId ? this.pending.get(message.requestId) : undefined;
      if (pending && message.requestId) {
        clearTimeout(pending.timer); this.pending.delete(message.requestId);
        pending.reject(new PartyRequestError(message.message, message.code, message.retryAfterMs));
        this.callbacks.pending?.();
      } else this.callbacks.error(message.message || 'That party action could not be completed.', message.code);
    }
  }

  private updateClock(serverTime: number, delay = (this.measuredLatency ?? 0) / 2) {
    if (!Number.isFinite(serverTime)) return;
    const measured = serverTime + delay - Date.now();
    if (!this.hasClock || Math.abs(this.serverOffsetMs - measured) > 5_000) this.serverOffsetMs = measured;
    else this.serverOffsetMs += (measured - this.serverOffsetMs) * 0.15;
    this.hasClock = true;
  }

  command(message: PartyCommand, key: string = message.type): Promise<CommandAck> {
    if (!this.connected) return Promise.reject(new PartyRequestError('The party is reconnecting. Please wait until everyone is back.', 'OFFLINE'));
    if (this.hasPending(key)) return Promise.reject(new PartyRequestError('That action is still being confirmed.', 'PENDING'));
    const requestId = crypto.randomUUID();
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(requestId);
        reject(new PartyRequestError('Confirmation took too long. Check the party before trying again; the action may have arrived.', 'TIMEOUT'));
        this.callbacks.pending?.();
        this.requestResync();
      }, commandTimeout);
      this.pending.set(requestId, { resolve, reject, timer, key });
      this.callbacks.pending?.();
      if (!this.sendRaw({ ...message, requestId } as ClientMessage)) {
        clearTimeout(timer); this.pending.delete(requestId);
        reject(new PartyRequestError('Your action was not sent. The party is reconnecting.', 'OFFLINE')); this.callbacks.pending?.();
      }
    });
  }

  private sendRaw(message: ClientMessage | 'ping') {
    if (this.socket?.readyState !== WebSocket.OPEN) return false;
    const text = typeof message === 'string' ? message : JSON.stringify(message);
    try { this.socket.send(text); this.sent += this.encoder.encode(text).byteLength; this.messagesOut++; return true; } catch { return false; }
  }

  /** Read-only state refresh; inventory is always calculated by the server. */
  resync() { return this.connected ? this.requestResync() : false; }

  private requestResync() {
    if (this.socket?.readyState !== WebSocket.OPEN) return false;
    if (this.waitingForSnapshot && this.status === 'syncing') return false;
    this.waitingForSnapshot = true;
    this.setStatus('syncing');
    const sent = this.sendRaw({ type: 'resync', lastSeq: this.world?.seq ?? 0 });
    this.armSnapshotTimeout();
    return sent;
  }

  private armSnapshotTimeout() {
    this.clearSnapshotTimeout();
    this.snapshotTimer = setTimeout(() => {
      this.snapshotTimer = null;
      if (this.waitingForSnapshot) this.socket?.close(4001, 'State refresh timed out');
    }, 12_000);
  }
  private clearSnapshotTimeout() { if (this.snapshotTimer) clearTimeout(this.snapshotTimer); this.snapshotTimer = null; }

  private ping() {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    if (this.pingSentAt > 0) {
      if (!document.hidden && performance.now() - this.pingSentAt > 55_000) this.socket.close(4000, 'Heartbeat timed out');
      return;
    }
    this.pingSentAt = performance.now();
    this.sendRaw('ping');
  }

  private scheduleReconnect() {
    if (this.stopped || this.reconnectTimer) return;
    this.attempt++;
    this.reconnectCount++;
    this.setStatus(navigator.onLine === false ? 'offline' : 'reconnecting');
    const delay = Math.min(25_000, 650 * 2 ** Math.min(this.attempt - 1, 6)) * (0.8 + Math.random() * 0.4);
    this.reconnectTimer = setTimeout(async () => {
      this.reconnectTimer = null;
      const generation = ++this.generation;
      try {
        await this.loadBootstrap(generation);
        if (!this.stopped && generation === this.generation) this.openSocket(generation);
      } catch (error) {
        if (this.stopped || generation !== this.generation) return;
        if (error instanceof PartyRequestError && error.code === 'ACCESS_REQUIRED') { this.setStatus('access-required'); this.callbacks.error(error.message, error.code); }
        else this.scheduleReconnect();
      }
    }, delay);
  }

  private visibilityChanged = () => {
    this.sendRaw({ type: 'visibility', visible: !document.hidden });
    if (!document.hidden) {
      if (this.connected) this.ping();
      else if (!this.stopped && this.status !== 'access-required') void this.retry();
    }
  };
  private becameOnline = () => { if (!this.stopped && this.status !== 'online' && this.status !== 'access-required') void this.retry(); };
  private becameOffline = () => { if (!this.stopped) this.setStatus('offline'); };

  private setStatus(status: ConnectionStatus) { this.status = status; this.callbacks.status(status, this.attempt); }
  private clearHeartbeat() { if (this.heartbeat) clearInterval(this.heartbeat); this.heartbeat = null; this.pingSentAt = 0; this.clearSnapshotTimeout(); }
  private clearReconnect() { if (this.reconnectTimer) clearTimeout(this.reconnectTimer); this.reconnectTimer = null; }
  private closeSocket() {
    this.clearHeartbeat();
    const socket = this.socket; this.socket = null;
    if (socket && socket.readyState < WebSocket.CLOSING) socket.close(1000, 'Opening a fresh connection');
    this.rejectPending('The connection changed before confirmation. Check the party before trying again.');
  }
  private rejectPending(message: string) {
    for (const pending of this.pending.values()) { clearTimeout(pending.timer); pending.reject(new PartyRequestError(message, 'DISCONNECTED')); }
    this.pending.clear(); this.callbacks.pending?.();
  }
  getStats(): NetworkStats {
    const now = performance.now();
    const elapsed = Math.max(0.05, (now - this.sampledAt) / 1000);
    const result = { latencyMs: this.measuredLatency, receivedBytes: this.received, sentBytes: this.sent, receivedPerSecond: (this.received - this.sampleReceived) / elapsed, sentPerSecond: (this.sent - this.sampleSent) / elapsed, reconnects: this.reconnectCount, messagesIn: this.messagesIn, messagesOut: this.messagesOut };
    this.sampledAt = now; this.sampleReceived = this.received; this.sampleSent = this.sent;
    return result;
  }
  dispose() {
    this.stopped = true; this.generation++; this.clearReconnect(); this.closeSocket(); this.setStatus('closed');
    document.removeEventListener('visibilitychange', this.visibilityChanged);
    window.removeEventListener('online', this.becameOnline); window.removeEventListener('offline', this.becameOffline);
  }
}
